import React, { useState, useEffect } from "react";
import { Bar, Pie } from "react-chartjs-2";
import { formatDateToYYYYMMDD } from "../helpers/DateHelpers";
import "../css/DashboardMain.css";
import SMSRecevied from "../../src/assets/Images/smsReceived.png";
import SMSPending from "../../src/assets/Images/SMSPending.png";
import SMSRejected from "../../src/assets/Images/SMSRejected.png";
import SMSTotal from "../../src/assets/Images/SMSTotal.png";
import { axiosPostApi } from "../../src/helpers/interceptorServices";
import { apiList } from "../../src/helpers/apiList";
import { MdHistory } from "react-icons/md";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import Loader from "./Loader/Loader";
import {
  emptyOrderScopeFilters,
  fetchOrderFilterOptions,
  cascadeOrderFilterOptions,
  changeOrderScopeFilter,
  orderScopeFilterFields,
  orderScopeOptionLabel,
} from "../helpers/orderFilterOptions";
import { useDispatch, useSelector } from "react-redux";
import {
  setBalance,
  setCustomerSummaryData,
} from "./store/balance/balanceSlice";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const DashboardMain = () => {
  const [loading, setLoading] = useState(true);
  const [orderOverviewData, setOrderOverviewData] = useState(null);
  const [oemSummary, setOemSummary] = useState([]);
  const [customerSummary, setCustomerSummary] = useState([]);
  const [orderHistoryData, setOrderHistoryData] = useState([]);
  const [overallProgressData, setOverallProgressData] = useState([]);
  const [lastFiveOrders, setLastFiveOrders] = useState([])
  const [dashboardData, setDashboardData] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);
  const [filters, setFilters] = useState(emptyOrderScopeFilters);

  const dispatch = useDispatch();
  const loggedInUserName = sessionStorage.getItem("username");
  const { userProfile } = useSelector((state) => state.auth);
  const userData = useSelector((state) => state.auth);

  // Dummy OEM summary data
  const dummyOemData = [
    { courier: "Blue Dart", sent: 120, delivered: 110, pending: 10 },
    { courier: "DTDC", sent: 95, delivered: 80, pending: 15 },
    { courier: "Delhivery", sent: 150, delivered: 140, pending: 10 },
  ];

  useEffect(() => {
    fetchOrderFilterOptions().then(setFilterOptions);
    // Simulate API loading
    setTimeout(() => {
      setLoading(false);
      setOemSummary(dummyOemData);
    }, 800);
  }, []);

  useEffect(() => {
    fetchDashboardData(filters);
  }, [filters]);

  const getOrderStatusStyle = (status) => {
  switch (status) {
    case "DELIVERED":
      return { color: "#16a34a", fontWeight: 600 }; // green
    case "IN_TRANSIT":
      return { color: "#2563eb", fontWeight: 600 }; // blue
    case "SENT":
      return { color: "#0284c7", fontWeight: 600 }; // cyan
    case "FAILED":
      return { color: "#dc2626", fontWeight: 600 }; // red
    case "CANCELLED":
      return { color: "#6b7280", fontWeight: 600 }; // gray
    case "PENDING":
      return { color: "#d97706", fontWeight: 600 }; // amber
    default:
      return { color: "#374151", fontWeight: 500 }; // neutral
  }
};

  const fetchDashboardData = (scopeFilters = emptyOrderScopeFilters) => {
    const endDate = formatDateToYYYYMMDD(new Date()); // Today's date formatted
    const startDate1 = new Date(new Date().setDate(new Date().getDate() - 90));
    const finalStartDate = formatDateToYYYYMMDD(startDate1);

    const requestBody = {
      startDate: finalStartDate,
      endDate: endDate,
      username: loggedInUserName,
      ...scopeFilters,
    };

    setLoading(true); // Set loading to true at the start

    axiosPostApi(apiList.DASHBOARD_DATA, requestBody)
      .then((response) => {
        if (response?.status === 200 && response?.data?.respCode === "00") {
          const dashboardData = response.data.data;
          setDashboardData(dashboardData);

            // 🔹 Order history (Pie chart)
            setOrderHistoryData(
              dashboardData.lastFiveOrders.map((o) => ({
                date: new Date(o.createdDate).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                }),
                orders: o.orderAmount,
              }))
            );

            setLastFiveOrders(dashboardData?.lastFiveOrders)

             // 🔹 Overall progress (Bar chart)
            setOverallProgressData([
              {
                date: "Orders",
                received: dashboardData.orders.total,
                dispatched:
                  dashboardData.orders.inTransit + dashboardData.orders.delivered,
              },
            ]);

          setOrderOverviewData({
            totalOrders: dashboardData.orders.total,
            inTransit: dashboardData?.orders?.inTransit,
            cancelled: dashboardData.orders.cancelled,
            failed: dashboardData.orders.failed,
            orderDelivered: dashboardData.orders.delivered,
          });

          dispatch(setBalance(response?.data?.data));
          dispatch(
            setCustomerSummaryData(response?.data?.customerSummaryModel)
          );
          setCustomerSummary(response?.data?.customerSummaryModel);
        } else if (response?.data?.respCode === "03") {
          // A filter the user can't use (e.g. stale selection): drop the filters
          console.error(response?.data?.message);
          setFilters(emptyOrderScopeFilters);
        } else {
          // Handle error case here if necessary
        }
      })
      .catch((error) => {
        console.error("Error fetching SMS overview data", error);
      })
      .finally(() => {
        setLoading(false); // Set loading to false once the request is complete
      });
  };

  return (
    <>
      {loading && <Loader />}
      <div className="pr-1">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h3 className="mb-0 dashboard-title font-medium">Overview</h3>
        </div>

        <OrderScopeFilterBar
          options={filterOptions}
          filters={filters}
          onChange={(key, value) =>
            setFilters((prev) => changeOrderScopeFilter(prev, key, value))
          }
          onClear={() => setFilters(emptyOrderScopeFilters)}
        />

        {/* LMBP details and balance (LMBP users) */}
        {userProfile === "CUSTOMER" && dashboardData?.wallet && (
          <LmbpDetailsCard wallet={dashboardData.wallet} />
        )}


        {/* Overview Cards */}
        <div
  className="mb-4"
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "16px",
  }}
>
  {[
    {
      title: "Total Orders",
      img: SMSTotal,
      value: orderOverviewData?.totalOrders ?? "Loading...",
    },
    {
      title: "Intransit",
      img: SMSRecevied,
      value: orderOverviewData?.inTransit ?? "Loading...",
    },
    {
      title: "Cancelled",
      img: SMSRejected,
      value: orderOverviewData?.cancelled ?? "Loading...",
    },
    {
      title: "Failed",
      img: SMSRejected,
      value: orderOverviewData?.failed ?? "Loading...",
    },
    {
      title: "Order Delivered",
      img: SMSPending,
      value: orderOverviewData?.orderDelivered ?? "Loading...",
    },
  ].map((item, idx) => (
    <div key={idx}>
      <div className="card p-3 h-100">
        <div className="d-flex align-items-center">
          <img
            src={item.img}
            alt={item.title}
            className="me-3"
            style={{ width: "40px", height: "40px" }}
          />
          <div>
            <h5 className="mb-1 font-medium">{item.title}</h5>
            <h3 className="font-medium text-[26px]">{item.value}</h3>
          </div>
        </div>
      </div>
    </div>
  ))}
        </div>

        {/* Charts Row */}
        <div className="row mb-4">
          {/* --- Order History Pie Chart --- */}
          <div className="col-md-8">
            <div className="card p-3">
              <h5 className="font-medium text-[20px] mb-2">Order History</h5>
              <div style={{ position: "relative", height: "250px" }}>
                <Pie
                  data={{
                    labels: orderHistoryData.map((i) => i.date),
                    datasets: [
                      {
                        data: orderHistoryData.map((i) => i.orders),
                        backgroundColor: [
                          "#00b6ce",
                          "#00d2a8",
                          "#fbc02d",
                          "#ff7043",
                          "#8e24aa",
                          "#29b6f6",
                          "#66bb6a",
                        ],
                        borderWidth: 2,
                        borderColor: "#fff",
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "right",
                        labels: { color: "#333", font: { size: 13 } },
                      },
                      tooltip: {
                        callbacks: {
                          label: (context) =>
                            `${context.label}: ${context.formattedValue} orders`,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>

          {/* --- Overall Progress: Received vs Dispatched --- */}
          <div className="col-md-4">
            <div className="card p-3">
              <h5 className="font-medium text-[20px] mb-2">Overall Progress</h5>
              <div style={{ position: "relative", height: "250px" }}>
                <Bar
                  data={{
                    labels: overallProgressData.map((i) => i.date),
                    datasets: [
                      {
                        label: "Orders Received",
                        data: overallProgressData.map((i) => i.received),
                        backgroundColor: "#00b6ce",
                        borderRadius: 5,
                      },
                      {
                        label: "Orders Dispatched",
                        data: overallProgressData.map((i) => i.dispatched),
                        backgroundColor: "#00d2a8",
                        borderRadius: 5,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: { color: "#333", font: { size: 12 } },
                      },
                    },
                    scales: {
                      x: { ticks: { color: "#555" }, grid: { display: false } },
                      y: {
                        beginAtZero: true,
                        ticks: { color: "#555" },
                        grid: { color: "#eee" },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ✅ OEM-wise Orders Summary Table */}
        {userProfile === "OPERATION" || userProfile === "HUB" ? (
          <div className="row">
            <div className="col-12">
              <div
                className="card"
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  padding: "16px",
                }}
              >
                <h5 className="font-medium text-[20px] flex items-center gap-2 mb-2">
                  <MdHistory /> OEM-wise Orders Summary
                </h5>
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Courier Partner</th>
                      <th>Orders Sent</th>
                      <th>Delivered</th>
                      <th>Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oemSummary.length > 0 ? (
                      oemSummary.map((row, idx) => (
                        <tr key={idx}>
                          <td>{row.courier}</td>
                          <td>{row.sent}</td>
                          <td>{row.delivered}</td>
                          <td>{row.pending}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center">
                          No OEM data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          // Summary Table
          <div className="row">
            <div className="col-12">
              <div
                className="card"
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  paddingRight: "16px",
                  paddingLeft: "16px",
                  paddingBottom: "16px",
                }}
              >
                {/* <h5
                  style={{ marginTop: "16px" }}
                  className="font-medium text-[20px] flex items-center gap-2 mb-2"
                >
                  <MdHistory /> Customer summary
                </h5>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Customer Code</th>
                      <th>Customer Name</th>
                      <th>User Id</th>
                      <th>Profile</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerSummary?.length > 0 ? (
                      customerSummary?.map((customer) => (
                        <tr key={customer?.customerCode}>
                          <td>{customer?.customerCode}</td>
                          <td>{customer?.name}</td>
                          <td>{userData?.user?.data?.username}</td>
                          <td>{userProfile}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4">No customer data available</td>
                      </tr>
                    )}
                  </tbody>
                </table> */}
                <h5 className="font-medium text-[20px] my-2">Last 5 Orders</h5>
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th className="!bg-[#ffe8d4]">Order ID</th>
                        <th className="!bg-[#ffe8d4]">Tracking</th>
                        <th className="!bg-[#ffe8d4]">Status</th>
                        <th className="!bg-[#ffe8d4]">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastFiveOrders.map(order => (
                        <tr key={order.orderId}>
                          <td>{order.orderId}</td>
                          <td>{order.trackingNumber}</td>
                          <td style={getOrderStatusStyle(order.orderStatus)}>{order.orderStatus}</td>
                          <td>₹{order.orderAmount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Top 10 LMBP Users */}
          {(userProfile === "OPERATION") && (
            <div className="row mt-4 pb-1">
              <div className="col-12">
                <div
                  className="card shadow-sm"
                  style={{
                    maxHeight: "320px",
                    overflowY: "auto",
                    padding: "16px",
                    borderRadius: "12px",
                  }}
                >
                  {/* Header */}
                  <h5 className="font-medium text-[20px] flex items-center gap-2 mb-3">
                    <MdHistory className="text-primary" />
                    Top 10 LMBP Users
                  </h5>
          
                  {/* Table */}
                  <table className="table table-striped table-hover align-middle">
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#f8fafc",
                        zIndex: 1,
                      }}
                    
                    >
                      <tr>
                        <th>Sr No</th>
                        <th>Retailer Name</th>
                        <th>Retailer ID</th>
                        <th className="text-center">Order Count</th>
                      </tr>
                    </thead>
          
                    <tbody>
                      {dashboardData?.topUsers?.length > 0 ? (
                        dashboardData.topUsers.slice(0, 10).map((user, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
          
                            <td className="fw-medium text-dark">
                              {user.retailerName}
                            </td>
          
                            <td>
                              <span className="badge bg-light text-dark border px-3 py-2">
                                {user.retailerId}
                              </span>
                            </td>
          
                            <td className="text-center">
                              <span className="badge bg-primary px-3 py-2">
                                {user.orderCount}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center text-muted py-3">
                            No Top Users data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
      </div>
    </>
  );
};

// Hub / LMBP / sub-LMBP / user filters for the order figures; a list the user
// has nothing in is hidden
const OrderScopeFilterBar = ({ options, filters, onChange, onClear }) => {
  if (!options) return null;
  const lists = cascadeOrderFilterOptions(options, filters);
  const fields = orderScopeFilterFields.filter(
    (f) => options[f.list]?.length > 0,
  );
  if (fields.length === 0) return null;
  const active = Object.values(filters).some(Boolean);

  return (
    <div
      className="card mb-3"
      style={{ borderRadius: "12px", padding: "12px 16px" }}
    >
      <div
        className="d-flex flex-wrap align-items-end"
        style={{ gap: "12px" }}
      >
        {fields.map((f) => (
          <div key={f.key} style={{ flex: "1 1 180px", minWidth: 0 }}>
            <label
              htmlFor={`dashboard-filter-${f.key}`}
              style={{ fontSize: "12px", color: "#6b7280", display: "block" }}
            >
              {f.label}
            </label>
            <select
              id={`dashboard-filter-${f.key}`}
              className="w-full px-2 py-2 h-[38px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ width: "100%" }}
              value={filters[f.key]}
              onChange={(e) => onChange(f.key, e.target.value)}
            >
              <option value="">All</option>
              {lists[f.list].map((item) => (
                <option key={item[f.valueKey]} value={item[f.valueKey]}>
                  {orderScopeOptionLabel(item, f.valueKey)}
                </option>
              ))}
            </select>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-outline-secondary"
          style={{ height: "38px" }}
          disabled={!active}
          onClick={onClear}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

const formatRupees = (value) =>
  `\u20b9 ${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// LMBP's own details and wallet balance, from the dashboard API's wallet block
const LmbpDetailsCard = ({ wallet }) => {
  const isSub = wallet.hierarchyType === "SUB";
  const parent = isSub
    ? [wallet.masterLmbpName, wallet.masterLmbpCode && `(${wallet.masterLmbpCode})`]
        .filter(Boolean)
        .join(" ")
    : wallet.hubCode;

  const details = [
    { label: "LMBP", value: [wallet.lmbpName, wallet.customerCode && `(${wallet.customerCode})`].filter(Boolean).join(" ") },
    { label: "Type", value: isSub ? "Sub-LMBP" : "Master LMBP" },
    { label: isSub ? "Master LMBP" : "Hub", value: parent },
    { label: "Rate card", value: wallet.rateCardName || wallet.rateCardId },
    { label: "Pincode", value: wallet.pincode },
  ];

  return (
    <div className="card mb-4" style={{ borderRadius: "12px", padding: "16px 20px" }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center" style={{ gap: "16px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "12px 24px",
            flex: "1 1 520px",
          }}
        >
          {details.map((d) => (
            <div key={d.label}>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>{d.label}</div>
              <div style={{ fontSize: "14px", fontWeight: 500 }}>{d.value || "-"}</div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "12px", color: "#6b7280" }}>Wallet balance</div>
          <div style={{ fontSize: "24px", fontWeight: 600, color: "#FF702B" }}>
            {formatRupees(wallet.balance)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardMain;
